import { useState } from 'react';
import './Pager.css';

/**
 * 공용 페이저 — 좌측 정보(총 N건 · 페이지 N/M · 페이지당 K건) + 우측 navigation
 * («, ‹, 윈도우 5 페이지, ›, ») + 페이지 직접 이동 입력 폼.
 * pageSizeOptions / onPageSizeChange 가 주어지면 페이지 크기 select 도 노출.
 * total === 0 이면 null. page 는 0-based.
 */
export function Pager({
  page,
  pageSize,
  total,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (p: number) => void;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
}): JSX.Element | null {
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const [jumpInput, setJumpInput] = useState('');

  if (total === 0) return null;

  const windowSize = 5;
  const half = Math.floor(windowSize / 2);
  let start = Math.max(0, page - half);
  const end = Math.min(totalPages - 1, start + windowSize - 1);
  start = Math.max(0, end - windowSize + 1);
  const pages: number[] = [];
  for (let i = start; i <= end; i++) pages.push(i);

  const submitJump = (e: React.FormEvent): void => {
    e.preventDefault();
    const n = parseInt(jumpInput, 10);
    if (!Number.isFinite(n)) return;
    const target = Math.max(0, Math.min(totalPages - 1, n - 1));
    onPageChange(target);
    setJumpInput('');
  };

  return (
    <div className="pager" role="navigation" aria-label="페이지 이동">
      <div className="pager__info">
        총 {total.toLocaleString('ko-KR')}건 · 페이지 {page + 1} / {totalPages} · 페이지당{' '}
        {pageSize}건
      </div>
      <div className="pager__nav">
        {pageSizeOptions && onPageSizeChange && (
          <label className="pager__size">
            크기
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              aria-label="페이지 크기"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          className="pager__btn"
          disabled={page === 0}
          onClick={() => onPageChange(0)}
          aria-label="첫 페이지"
        >
          «
        </button>
        <button
          type="button"
          className="pager__btn"
          disabled={page === 0}
          onClick={() => onPageChange(page - 1)}
          aria-label="이전"
        >
          ‹
        </button>
        {start > 0 && <span className="pager__ellipsis">…</span>}
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            className={p === page ? 'pager__btn pager__btn--active' : 'pager__btn'}
            onClick={() => onPageChange(p)}
            aria-current={p === page ? 'page' : undefined}
          >
            {p + 1}
          </button>
        ))}
        {end < totalPages - 1 && <span className="pager__ellipsis">…</span>}
        <button
          type="button"
          className="pager__btn"
          disabled={page + 1 >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label="다음"
        >
          ›
        </button>
        <button
          type="button"
          className="pager__btn"
          disabled={page + 1 >= totalPages}
          onClick={() => onPageChange(totalPages - 1)}
          aria-label="마지막 페이지"
        >
          »
        </button>
        <form className="pager__jump" onSubmit={submitJump}>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={jumpInput}
            onChange={(e) => setJumpInput(e.target.value)}
            placeholder={`1-${totalPages}`}
            aria-label="페이지 번호 입력"
          />
          <button type="submit" className="pager__btn">
            이동
          </button>
        </form>
      </div>
    </div>
  );
}
